import React, { useEffect } from 'react';
import MessageCard from '../card';
import type { MessageCardProps } from '../card';
import { Role } from '@/pages/chat/type';
import { ContentType } from '../chatBox/util';
import { parseC1Response } from './c1Response';
import C1AnswerCard from './C1AnswerCard';
import { renderNavigationAnswer } from './NavigationAnswerCard';
import type { Message } from '../chatBox';

interface AgentAnswerProps extends MessageCardProps {
  onNavigationOptionClick?: (option: string) => void;
}

export const getAgentAnswerParts = (msg: Message) =>
  msg.agentCode === 'navigation-agent' ? (msg.parts?.filter(part => part.type !== 'text') ?? []) : (msg.parts ?? []);

export const hasAgentAnswer = (msg: Message) =>
  msg.agentCode === 'navigation-agent' &&
  !!msg.answerFinished &&
  !!msg.structuredOutputs?.some(output => output.contentType === ContentType.NAVIGATION);

const NavigationAnswer = ({ onNavigationOptionClick, ...props }: AgentAnswerProps) => {
  const msg = props.msg!;
  const outputs = msg.answerFinished
    ? (msg.structuredOutputs?.filter(output => output.contentType === ContentType.NAVIGATION) ?? [])
    : [];
  return (
    <MessageCard {...props} msg={{ ...msg, parts: getAgentAnswerParts(msg) }} fullWidth hideOperations>
      {outputs.length
        ? outputs.map((output, index) => renderNavigationAnswer(output.data, index, onNavigationOptionClick))
        : undefined}
    </MessageCard>
  );
};

const C1Answer = (props: MessageCardProps) => {
  const msg = props.msg!;
  const parts = msg.parts ?? [];
  const answers = parts.map(part => (part.type === 'text' ? parseC1Response(part.data) : null));
  const hasTemplate = answers.some(Boolean);
  const lastText = [...parts].reverse().find(part => part.type === 'text' || part.type === 'markdown');
  const lastTextIsTemplate = lastText?.type === 'text' && !!parseC1Response(lastText.data);
  useEffect(() => {
    if (lastTextIsTemplate) props.onRenderComplete?.(String(lastText?.data ?? ''));
  }, [lastTextIsTemplate, lastText?.data, props.onRenderComplete]);
  if (!hasTemplate) return <MessageCard {...props} />;
  const copyText = parts
    .filter(part => ['text', 'markdown', 'error'].includes(part.type))
    .map(part =>
      part.type === 'text' && parseC1Response(part.data)
        ? JSON.stringify(parseC1Response(part.data), null, 2)
        : part.data,
    )
    .join('\n');
  return (
    <MessageCard
      {...props}
      msg={{ ...msg, parts: parts.filter((_, index) => !answers[index]) }}
      hideOperations
      copyText={copyText}
    >
      {answers.map((answer, index) => (answer ? <C1AnswerCard key={index} data={answer} /> : null))}
    </MessageCard>
  );
};

const AgentAnswer = ({ onNavigationOptionClick, ...props }: AgentAnswerProps) => {
  if (props.msg?.role !== Role.ASSISTANT) return <MessageCard {...props} />;
  switch (props.msg.agentCode) {
    case 'navigation-agent':
      return <NavigationAnswer {...props} onNavigationOptionClick={onNavigationOptionClick} />;
    case 'display-c1':
      return <C1Answer {...props} />;
    default:
      return <MessageCard {...props} />;
  }
};

export default AgentAnswer;
